/**
 * useDocsGateStageChange — reusable helper for any recruiter surface that
 * moves an application to a new stage.
 *
 * Flow:
 *   1. On changeStage(appId, targetStage, {candidate_id, candidate_name}):
 *      - GET /candidates/{cid}/doc-freshness → figure out if targetStage is a
 *        gate stage AND there are missing docs.
 *      - If yes, open the modal. User can:
 *          - upload docs on the candidate page (link) then retry,
 *          - or type a reason (required for `block`, optional for `warn`)
 *            and hit "Move anyway".
 *      - Otherwise, just PATCH normally.
 *   2. Reason (if any) is sent as `docs_gate_override_reason` — server
 *      writes a `docs_gate_bypassed` audit row.
 */
import { useCallback, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, FileText, ExternalLink } from "lucide-react";

export function useDocsGateStageChange() {
    const [state, setState] = useState(null);
    const [reason, setReason] = useState("");
    // state: { appId, stage, candidate_id, candidate_name, missing, enforcement, resolve }

    const changeStage = useCallback((appId, stage, opts = {}) => {
        return new Promise(async (resolve) => {
            // Preflight — cheap 200 in the majority (no-gate) case.
            let gateResult = null;
            if (opts.candidate_id) {
                try {
                    const { data } = await api.get(`/candidates/${opts.candidate_id}/doc-freshness`);
                    const gateStages = ((data?.config?.gate_stages) || []).map((s) => (s || "").toLowerCase());
                    const isGate = gateStages.includes((stage || "").toLowerCase());
                    if (isGate && Array.isArray(data?.missing) && data.missing.length) {
                        gateResult = {
                            missing: data.missing,
                            enforcement: data.config?.enforcement || "warn",
                        };
                    }
                } catch {
                    // Best-effort — fall through to raw PATCH if the preflight fails
                }
            }
            if (!gateResult) {
                await _submitPatch(appId, stage, null, resolve);
                return;
            }
            // Open modal
            setReason("");
            setState({
                appId, stage,
                candidate_id: opts.candidate_id,
                candidate_name: opts.candidate_name,
                missing: gateResult.missing,
                enforcement: gateResult.enforcement,
                resolve,
            });
        });
    }, []);

    const proceed = async () => {
        if (!state) return;
        const trimmed = (reason || "").trim();
        if (state.enforcement === "block" && !trimmed) return;
        setState(null);
        await _submitPatch(state.appId, state.stage, trimmed || "warn-acknowledged", state.resolve);
    };

    const cancel = () => {
        if (state?.resolve) state.resolve({ ok: false, cancelled: true });
        setState(null);
    };

    const modal = (
        <Dialog open={!!state} onOpenChange={(o) => { if (!o) cancel(); }}>
            <DialogContent className="sm:max-w-lg" data-testid="docs-gate-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-amber-700">
                        <AlertTriangle className="h-5 w-5" />
                        Documents pending
                    </DialogTitle>
                    <DialogDescription>
                        Moving <span className="font-semibold">{state?.candidate_name || "this candidate"}</span> to
                        {" "}<span className="font-semibold">{state?.stage}</span> — these employment documents
                        are still missing:
                    </DialogDescription>
                </DialogHeader>
                <div className="border border-amber-200 bg-amber-50 rounded p-3 space-y-1">
                    {(state?.missing || []).map((m, i) => (
                        <div key={i} className="text-sm flex items-center gap-2" data-testid={`docs-gate-missing-${i}`}>
                            <FileText className="h-4 w-4 text-amber-700 shrink-0" />
                            <span className="font-medium">{m.slot_label}</span>
                            <span className="text-muted-foreground">— {m.exp_label}</span>
                        </div>
                    ))}
                </div>
                {state?.candidate_id && (
                    <Link
                        to={`/candidates/${state.candidate_id}`}
                        target="_blank"
                        className="text-sm text-ikb inline-flex items-center gap-1 hover:underline"
                        data-testid="docs-gate-open-candidate"
                    >
                        Open candidate to attach docs <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                )}
                <div>
                    <div className="text-sm font-medium mb-1">
                        {state?.enforcement === "block"
                            ? "Override reason (required to proceed)"
                            : "Override reason (optional)"}
                    </div>
                    <Textarea
                        data-testid="docs-gate-reason"
                        placeholder="e.g. Documents in transit — will attach EOD"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                    <div className="text-[11px] text-muted-foreground mt-1">
                        A note will be logged on the candidate Activity tab.
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={cancel} data-testid="docs-gate-cancel">Cancel</Button>
                    <Button
                        data-testid="docs-gate-proceed"
                        disabled={state?.enforcement === "block" && !reason.trim()}
                        onClick={proceed}
                    >
                        Move anyway
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    return { changeStage, modal };
}


async function _submitPatch(appId, stage, overrideReason, resolve) {
    try {
        const body = { stage };
        if (overrideReason) body.docs_gate_override_reason = overrideReason;
        const { data } = await api.patch(`/applications/${appId}`, body);
        if (overrideReason) {
            toast.success("Stage updated — override recorded on Activity");
        }
        resolve({ ok: true, data });
    } catch (err) {
        toast.error(formatApiError(err));
        resolve({ ok: false, error: err });
    }
}
